import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import strapi from "scripts/strapi";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import BasicCardSection from "@/sections/basicCards";
import GraphicSvg from "./graphic.svg";
import FlowChartSection from "@/sections/flowChart";
import MapLocationDotSvg from "/public/images/icons/mapLocationDot.svg";
import StripedCtaSection from "@/sections/stripedCta";
import FilterCircleDollarSvg from "/public/images/icons/filterCircleDollar.svg";
import CrosshairsSvg from "/public/images/icons/crosshairs.svg";
import UsersViewfinderSvg from "/public/images/icons/usersViewfinder.svg";
import FilePen from "/public/images/icons/filePen.svg";

export default function UserAcquisition({articles}) {
  const [elements, setElements] = useState({
    fold: {
      infographic: useInView(inViewSettings((targets) => {
        let graphic = targets[0];
        let tl = gsap.timeline({});

        let bars = graphic.querySelectorAll(".bar");

        let texts = graphic.querySelectorAll(".text");

        tl.set(graphic, {visibility: "visible", opacity: 1});
        tl.fromTo(bars, {
          opacity: 0,
          translateY: "-25px",
          transformOrigin: "center top",
        }, {
          opacity: 1,
          scaleY: 1,
          translateY: "0%",
          duration: 0.25,
          ease: "sine.inOut",
          stagger: {
            each: 0.15,
          },
        });
      })),
    },
    explanation: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
    },
    explanation2: {
      text1: useInView(inViewSettings(slideFadeIn)),
      headline1: useInView(inViewSettings(slideFadeIn)),
      text2: useInView(inViewSettings(slideFadeIn)),
      headline2: useInView(inViewSettings(slideFadeIn)),
    },
  });

  return <GenericLayout
    title="Scale Your User Acquisitions | Deviate Labs"
    description="Let Deviate Labs grow your user base, increase sales and maximize your ROI."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>Scale Your</div>
        <div>User Acquisitions</div>
      </>}
      className={css.fold}
      subheadline="Let Deviate Labs grow your user base, increase sales and maximize your ROI."
      image={
        <div className={`target ${css.graphic}`} ref={elements.fold.infographic.ref}>
          <GraphicSvg/>
        </div>
      }
    />



    <FlowChartSection
      headline="Your Formula For Fast Growth"
      subheadline="We optimize every stage of your customer acquisition journey."
      cards={[
        {
          headline: "Full Funnel Customer Acquisition Audit",
          body: "Our team conducts a comprehensive audit of your entire customer acquisition funnel, from initial brand awareness to final conversion. We identify friction points and opportunities through in-depth analytics and user research. Then we work collaboratively to ideate and create an optimized funnel strategy with your goals in mind. This results in increased conversion rates, lower cost per acquisition, and fully aligned messaging across channels.",
          icon: <FilterCircleDollarSvg/>,
        },
        {
          headline: "Precision Targeted Tracking Data",
          body: "We implement comprehensive tracking and analytics across your digital presence. This allows us to collect robust behavioral data on your users and customers. Our data experts analyze this information to gain actionable insights into user journeys, pain points, and motivations. This enables us to create personalized experiences, optimize conversion paths, and make smarter data-driven decisions for your business.",
          icon: <CrosshairsSvg/>,
        },
        {
          headline: "Behavioural Cohort Based Ad Optimization",
          body: "We use advanced segmentation based on user behaviors and attributes to identify high-value cohorts who are primed for your products or services. Then we craft targeted ad campaigns with messaging that resonates with each cohort. This increases engagement and lowers the cost per click.",
          icon: <UsersViewfinderSvg/>,
        },
        {
          headline: "Outstanding Creatives and Copy",
          body: "Our talented team of designers and copywriters excels at developing compelling ads. We ensure your campaigns stand out and connect emotionally with your audience. Our assets inspire action while maintaining your brand identity through consistent messaging and visuals.",
          icon: <FilePen/>,
        },
      ]}
    />


    <section className={`section ${css.explanation1}`}>
      <div className="sectionInner">
        <h2 ref={elements.explanation.headline.ref}>
          <SplitText className="target" text="Relentless Focus On ROAS"/>
        </h2>

        <div className={css.text} ref={elements.explanation.text.ref}>
          <p>
            <SplitText className="target" text="Deviate Labs is an industry leading growth marketing agency trusted with large enterprise scale advertising budgets because of our ability to consistently generate high performance for our clients' key performance metrics. Our multi-step approach assesses and revitalizes your conversion funnel and tracking data, so we can train ad campaigns and maximize your Return on Ad Spend (ROAS)."/>
          </p>
        </div>
      </div>
    </section>

    <BasicCardSection
      headline="Your Formula For Fast Growth"
      subheadline=""
      width="2"
      color="cyan"
      cards={[
        {
          name: "Strategic User Acquisition Tactics",
          body: <>Investing in a customer acquisition strategy is critical for you to attract the right leads to your company and increase your chances of retaining them for the long term.</>,
        },
        {
          name: "What is Customer Acquisition?",
          body: <>
            <p>Customer acquisition is the process of getting potential customers to buy your products. A strong customer acquisition strategy:</p>
            <ol>
              <li>Attracts quality leads</li>
              <li>Nurtures them until they become sales-ready</li>
              <li>And converts them into customers</li>
            </ol>
            <p>The overall cost of these steps is referred to as your customer acquisition cost (CAC).</p>
          </>,
        },
        {
          name: "The Purpose of Customer Acquisition",
          body: <>The purpose of customer acquisition is to find a repeatable, methodical way of attracting customers to your business. While you can wait for customers to naturally come to you, that won&apos;t guarantee that you can increase, or even maintain, profits over time.</>,
        },
        {
          name: "Customer Acquisition vs Marketing",
          body: <>While marketing aims to build awareness, customer acquisition looks to drive action. Acquisition focuses on what happens after potential customers click through to your page or receive your emails. If they&apos;re willing to take action by purchasing your products or services, that&apos;s acquisition.</>,
        },
      ]}
    />

    <TestimonialsSpotlight testimonial={TESTIMONIALS.BADASS_BEARD_CARE}/>


    <section className={`section ${css.explanation2}`}>
      <div className={`sectionInner ${css.sectionInner}`}>
        <div>
          <h2 ref={elements.explanation2.headline1.ref}>
            <SplitText className="target" text="Diversify Your Customer Acquisition Strategy"/>
          </h2>

          <div className={css.text} ref={elements.explanation2.text1.ref}>
            <p>
              <SplitText className="target" text="Boost your acquisition efforts by diversifying your approach. We will help you analyze your current marketing funnel, explore effective alternative channels and methods to reach new audiences and generate more high-quality leads. With a data driven conversion strategy, you can increase your chances of acquiring valuable customers."/>
            </p>
          </div>
        </div>
        <div>
          <h2 ref={elements.explanation2.headline2.ref}>
            <SplitText className="target" text="Maximize Your ROAS with a Data-Driven Approach"/>
          </h2>

          <div className={css.text} ref={elements.explanation2.text2.ref}>
            <p>
              <SplitText className="target" text="Our team will assess and revitalize your conversion funnel and tracking data, ensuring that every step of the customer journey is optimized for success. By analyzing your data and understanding the unique nuances of your business, we can develop highly targeted and effective ad campaigns, enabling us to optimize campaigns and drive maximum results."/>
            </p>
          </div>
        </div>
      </div>
    </section>

    <StripedCtaSection
      headline="Let's Grow Your User Base Together"
      ctaHref="#contact"
      description="Ready to take your user acquisition to the next level? Contact us today to schedule a consultation. We'll work closely with you to develop a tailored strategy that drives fast growth and maximizes your ROI."
    />


    <TacticsSmall
      headline="We Offer a Full Range of SEO Services"
      subheadline="Whatever your goals, we have the expertise to optimize your growth."
      enabled={[
        TACTICS.HIGH_PERFORMANCE_LANDING_PAGES,
        TACTICS.CONTENT_MARKETING,
        TACTICS.SONIC_BRANDING,
        TACTICS.INFLUENCER_MARKETING,
        TACTICS.B2B_SAAS_GROWTH_MARKETING,
      ]}
    />

    <ContactSection/>


  </GenericLayout>;
}


export async function getStaticProps() {
  let data = await strapi.article.findMany({
    filters: {
      id: {
        $eq: [33, 32, 27],
      },
    },
    populate: {
      coverArt: "*",
    },
    fields: [ //don't include the body since it's large and unnecessary
      "headline",
      "slug",
      "teaser",
      "priority",
      "category",
      "publishedAt",
    ],
  });

  return {
    props: {
      articles: data,
    },
    revalidate: 60,
  };
}
